// Phase 2 Type Definitions for TheAdsFather Frontend

import { getFrontendAppDeployment } from '../utils/appDeployment';

export interface AdMetadata {
  production_id: number;     // Numeric production ID (e.g., 1337)
  creative_number: number;   // Numeric creative number starting from 10000
  ratio: string;            // Ratio string (e.g., "16:9", "9:16", "1:1")
  name_en: string;          // English name (required for identifier)
  language: string;         // Language code (e.g., "EN", "ES", "FR")
  filename: string;         // Original filename
  created_by: string;       // User who created the ad
  watchers: string[];       // Array of Slack user IDs
  duration?: number;        // Optional manual duration in seconds
  tags?: {                  // Optional tag metadata
    licensedSong?: string[];
    cast: 'Kids' | 'Couple' | 'Solo actor' | 'No Human actor' | 'Human with Prompt' | 'hand only' | 'Family' | 'unknown';
    heroAge: 'Senior' | 'Adult' | 'Teenage' | 'Kid' | 'unknown';
    heroGender: 'Female' | 'Male' | 'other' | 'unknown';
    pianoType?: 'Piano' | 'Keyboard' | 'Mixed' | 'No instrument' | 'unknown';
    appDevice?: 'phone' | 'tablet' | 'both' | 'no device' | 'unknown';
    meantTo?: string[];
    audience?: string[];
    seasonal?: string[];
    contentShownInApp?: string[];
  };
}

// New types for batch upload mode
export interface FileWithStatus extends File {
  id: string;
  status: FileStatus;
  metadata?: AdMetadata;
  validation?: {
    isValid: boolean;
    error?: string;
  };
  preview?: {
    duration?: number;
    width?: number;
    height?: number;
  };
}

export enum FileStatus {
  NEEDS_METADATA = 'needs_metadata',  // "?" - needs more information
  READY = 'ready',                    // "V" - has all metadata and ready to upload
  INVALID = 'invalid'                 // validation failed
}

// New wizard-specific enums and types
export enum UploadStep {
  FILE_SELECTION = 'file_selection',
  PRODUCTION_SELECTION = 'production_selection', 
  CREATIVE_ASSIGNMENT = 'creative_assignment',
  SHARED_METADATA = 'shared_metadata',
  INDIVIDUAL_METADATA = 'individual_metadata',
  REVIEW_UPLOAD = 'review_upload'
}

export enum CreativeAssignmentMode {
  INDIVIDUAL = 'individual',
  SHARED = 'shared'
}

export interface SharedMetadata {
  name_en: string;
  language: string;
  watchers: string[];
  created_by: string;
}

export interface UploadWizardState {
  currentStep: UploadStep;
  files: FileWithStatus[];
  productionId: number | null;
  creativeAssignmentMode: CreativeAssignmentMode | null;
  sharedCreativeId: number | null;
  sharedMetadata: SharedMetadata | null;
  individualMetadata: Map<string, AdMetadata>;
  stepValidation: Record<UploadStep, boolean>;
  isUploading: boolean;
}

export interface StepValidationResult {
  isValid: boolean;
  errors: string[];
  warnings: string[];
}

export interface UniqueComboValidationRequest {
  production_id: number;
  creative_number: number;
  ratio: string;
}

export interface UniqueComboValidationResponse {
  isUnique: boolean;
  conflicts: Array<{
    ad_id: string;
    production_id: number;
    creative_number: number;
    ratio: string;
    name_en: string;
  }>;
}

export interface AdRecord {
  ad_id: string;            // Format: "{prod}_{creative}_{english_name}_{language}_{ratio}_{duration_in_seconds}"
  production_id: number;
  creative_number: number;
  ratio: string;
  name_en: string;          // English name (required for identifier)
  language: string;         // Language code (e.g., "EN", "ES", "FR")
  video_url: string;        // S3 URL for stored video
  file_size: number;
  duration: number;
  status: AdStatus;
  watchers: string[];
  youtube_job_id?: string;
  youtube_video_id?: string;
  meta_job_id?: string;
  meta_ad_id?: string;
  meta_ad_ids?: Record<string, string>;
  applovin_job_id?: string;
  applovin_asset_id?: string;
  applovin_creative_set_id?: string;
  metadata: Record<string, any>;
  disabled: boolean;
  created_at: string;
  updated_at: string;
  created_by: string;
  tags?: {                  // Tag metadata
    licensedSong: string[] | string; // Support both new (array) and legacy (string) formats
    cast: 'Kids' | 'Couple' | 'Solo actor' | 'No Human actor' | 'Human with Prompt' | 'hand only' | 'Family' | 'unknown';
    heroAge: 'Senior' | 'Adult' | 'Teenage' | 'Kid' | 'unknown';
    heroGender: 'Female' | 'Male' | 'other' | 'unknown';
    pianoType?: 'Piano' | 'Keyboard' | 'Mixed' | 'No instrument' | 'unknown';
    appDevice?: 'phone' | 'tablet' | 'both' | 'no device' | 'unknown';
    meantTo?: string[];
    audience?: string[];
    seasonal?: string[];
    contentShownInApp?: string[];
  };
  performance_7d?: PlatformPerformanceBreakdown;
  performance_30d?: PlatformPerformanceBreakdown;
  performance_all?: PlatformPerformanceBreakdown;
  performance_updated_at?: string;
  has_activity_7d?: boolean;
}

export interface PerformanceMetrics {
  spend: number;
  impressions: number;
  clicks: number;
  days_with_spend: number;
}

export interface PlatformPerformanceBreakdown {
  meta?: PerformanceMetrics;
  youtube?: PerformanceMetrics;
  applovin?: PerformanceMetrics;
}

export interface ProductionRecord {
  production_id: number;
  production_name: string;
  description?: string;
  created_by: string;
  ad_count: number;
  created_at: string;
  updated_at: string;
  status: ProductionStatus;
  production_performance_7d?: PlatformPerformanceBreakdown;
  production_performance_30d?: PlatformPerformanceBreakdown;
  production_performance_all?: PlatformPerformanceBreakdown;
  production_performance_updated_at?: string;
}

export interface UploadSession {
  session_id: string;
  s3_key: string;
  presigned_url: string;
  post_url?: string;
  post_fields?: Record<string, string>;
  put_url?: string;
  metadata: AdMetadata;
  expires_at: string;
  bucket: string;
  content_type: string;
  max_file_size: number;
}

export interface UploadProgress {
  sessionId: string;
  filename: string;
  progress: number;        // 0-100
  status: UploadStatus;
  uploadedBytes: number;
  totalBytes: number;
  error?: string;
}

export interface SlackUser {
  id: string;
  name: string;
  display_name?: string;
  real_name?: string;
  profile_image?: string;
  is_active: boolean;
}

export interface ValidationResult {
  is_valid: boolean;
  is_unique?: boolean;
  error_message?: string;
  error_code?: string;
}

export interface ApiResponse<T = any> {
  data?: T;
  error?: boolean;
  message?: string;
  error_code?: string;
  timestamp?: string;
}

// Enums
export enum AdStatus {
  UPLOADED = 'uploaded',
  REVIEWED = 'reviewed', 
  APPROVED = 'approved',
  PUBLISHED = 'published'
}

export enum ProductionStatus {
  ACTIVE = 'active',
  COMPLETED = 'completed',
  ARCHIVED = 'archived'
}

export enum UploadStatus {
  IDLE = 'idle',
  UPLOADING = 'uploading',
  PROCESSING = 'processing',
  COMPLETED = 'completed',
  ERROR = 'error',
  CANCELLED = 'cancelled'
}

export enum NotificationEventType {
  AD_UPLOADED = 'ad_uploaded',
  AD_REVIEWED = 'ad_reviewed',
  AD_APPROVED = 'ad_approved',
  AD_PUBLISHED = 'ad_published'
}

// Job-related enums
export enum JobStatus {
  PENDING = 'pending',
  PROCESSING = 'processing',
  UPLOADING = 'uploading',
  COMPLETED = 'completed',
  FAILED = 'failed',
  CANCELLED = 'cancelled'
}

export const MEANT_TO_PLATFORMS = ['UAC iOS', 'UAC Android', 'Meta', 'AppLovin'] as const;

// Additional "Meant To" platforms available only on the Piano deployment.
export const PIANO_ONLY_MEANT_TO_PLATFORMS = ['Meta App', 'Meta Webflow', 'PB1'] as const;

/**
 * Returns the list of selectable "Meant To" platforms for the current app
 * deployment. Piano gets the base platforms plus its own extras; all other
 * apps (Draw, Guitar, ...) get only the base platforms.
 */
export const getMeantToPlatforms = (): string[] => {
  if (getFrontendAppDeployment() === 'piano') {
    return [...MEANT_TO_PLATFORMS, ...PIANO_ONLY_MEANT_TO_PLATFORMS];
  }
  return [...MEANT_TO_PLATFORMS];
};

export enum JobPlatform {
  YOUTUBE = 'youtube',
  META = 'meta'
}

export enum JobSource {
  SLACK_DM = 'slack_dm',
  GOOGLE_DRIVE = 'google_drive',
  WEB_INTERFACE = 'web_interface'
}

// Component Props Types
export interface DropZoneProps {
  onFilesSelected: (files: File[]) => void;
  accept: string[];
  maxSize: number;
  multiple?: boolean;
  disabled?: boolean;
}

// Enhanced props for batch mode
export interface BatchDropZoneProps {
  onFilesChange: (files: FileWithStatus[]) => void;
  onFileClick: (file: FileWithStatus) => void;
  onFileDelete: (fileId: string) => void;
  files: FileWithStatus[];
  accept: string[];
  maxSize: number;
  disabled?: boolean;
}

export interface MetadataModalProps {
  isOpen: boolean;
  file: File;
  onSubmit: (metadata: AdMetadata) => void;
  onCancel: () => void;
  suggestedCreativeNumber?: number;
}

// Enhanced props for batch metadata modal
export interface BatchMetadataModalProps {
  isOpen: boolean;
  file: FileWithStatus | null;
  onSubmit: (fileId: string, metadata: AdMetadata) => void;
  onCancel: () => void;
  suggestedCreativeNumber?: number;
}

export interface ProgressTrackerProps {
  uploads: UploadProgress[];
  onCancel?: (sessionId: string) => void;
  onRetry?: (sessionId: string) => void;
}

export interface WatcherPickerProps {
  selectedWatchers: string[];
  onWatchersChange: (watchers: string[]) => void;
  disabled?: boolean;
}

export interface ProductionListProps {
  onProductionSelect?: (production: ProductionRecord) => void;
  createdBy: string;
}

export interface AdListProps {
  productionId: number;
  onAdSelect?: (ad: AdRecord) => void;
}

// Form Types
export interface ProductionFormData {
  production_name: string;
  description?: string;
  created_by: string;
}

export interface AdUploadFormData extends AdMetadata {
  files: File[];
}

// API Request/Response Types
export interface InitiateUploadRequest {
  production_id: number;
  creative_number: number;
  ratio: string;
  filename: string;
  created_by: string;
  name_en: string;
  language: string;
  watchers?: string[];
  content_type?: string;
}

export interface InitiateUploadResponse {
  upload_session_id: string;
  presigned_url: string;
  post_url?: string;
  post_fields?: Record<string, string>;
  put_url?: string;
  s3_key: string;
  bucket: string;
  content_type: string;
  max_file_size: number;
  expires_at: string;
}

export interface CompleteUploadRequest {
  session_id: string;
  s3_key: string;
}

export interface CompleteUploadResponse {
  ad_id: string;
  status: AdStatus;
  ad_record: AdRecord;
}

export interface ValidateIdentifierRequest {
  production_id: number;
  creative_number: number;
  ratio: string;
}

export interface ValidateIdentifierResponse {
  is_unique: boolean;
  error_message?: string;
}

export interface SuggestCreativeNumberResponse {
  suggested_creative_number: number;
}

export interface SearchWatchersRequest {
  q: string;
  limit?: number;
}

export interface SearchWatchersResponse {
  watchers: SlackUser[];
  query: string;
  total: number;
}

// Error Types
export interface UploadError extends Error {
  code?: string;
  details?: any;
} 